import React, { useEffect, useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Spinner } from '@iq/react-components';
import {
  requestSources,
  getSources,
  getSourcesLoaded,
  createVariables,
  updateVariable,
  deleteVariable,
} from '../../../bundles/sources';
import { getActiveSite } from '../../../bundles/sites';
import {
  checkIsOnline,
  displayNotification,
  getNotification,
} from '../../../bundles/notifications';
import {
  getComponentsLoaded,
  getStaticComponents,
  requestComponents,
} from '../../../bundles/components';
import { useDebounce, useClientSize, cleanFormData, withTypedValues } from '../../../utils';
import { INTERNAL_DATA_SOURCE } from '../../../constants';
import { getVariables } from '../../../services';
import ComponentSelect from '../../ComponentSelect';
import ListItem from '../../ListItem';
import EditItemModal from '../../EditItemModal';
import Heading from '../../Heading';
import Pagination from '../../Pagination';
import {
  addRequiredFieldErrors,
  suppressRequiredFieldErrors,
} from '../../JsonEditor/validationUtils';
import { getAttributeSchema, attributeUiSchema } from '../ComponentView/schemas/attributeSchema';

const COLUMN_WIDTHS = {
  type: 'percent',
  widths: [22, 15, 22, 22, 19],
};

const AttributeView = () => {
  const dispatch = useDispatch();
  const { id: siteId, org } = useSelector(getActiveSite);
  const sources = useSelector(getSources);
  const sourcesLoaded = useSelector(getSourcesLoaded);
  const componentsLoaded = useSelector(getComponentsLoaded);
  const components = useSelector(getStaticComponents);

  const [attribute, setAttribute] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [compFilter, setCompFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [attributes, setAttributes] = useState(null);
  const [totalAttributes, setTotalAttributes] = useState(0);
  const [needsRefresh, setNeedsRefresh] = useState(0);
  const [extraErrors, setExtraErrors] = useState({});
  const [headerHeight, setHeaderHeight] = useState(0);

  const [{ height }, clientReference] = useClientSize();

  const isHeightReady = height > 0 && headerHeight > 0;
  // list-container - List-header - spacing 2rem(32)
  // Intentionally omitting headerHeight: including it changes the pagination limit
  // mid-session, which is a separate behavior change from this fix.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const queryLimit = useMemo(() => Math.floor((height - headerHeight - 32) / 51), [height]);

  useLayoutEffect(() => {
    const headerElement = document.querySelector('.list-container .list-item--header');
    if (headerElement) {
      const ListHeaderHeight = headerElement.getBoundingClientRect().height;
      setHeaderHeight(ListHeaderHeight);
    }
  }, [attributes]);

  useEffect(() => {
    if (siteId && sourcesLoaded !== siteId) {
      dispatch(requestSources({ siteId }));
    }
  }, [siteId, sourcesLoaded, dispatch]);

  const internalSiteSource = sources.find((source) => source.type === INTERNAL_DATA_SOURCE);

  const baseQuery = useMemo(
    () => ({
      limit: queryLimit,
      page,
      order: 'ASC',
      isAttribute: true,
    }),
    [queryLimit, page]
  );

  const onRequestAttributes = useDebounce(() => {
    const query = { ...baseQuery };
    if (searchFilter) {
      query.search = searchFilter;
    }
    if (compFilter) {
      query.assetId = compFilter;
    }

    const getAttributes = async () => {
      try {
        const { values, total } = await getVariables(internalSiteSource.id, query);
        setAttributes(values.map((value) => withTypedValues(value)));
        setTotalAttributes(total);
      } catch (error) {
        console.error('Unable to fetch attributes:', error);
        dispatch(checkIsOnline());
        dispatch(displayNotification(getNotification('getVariables', 'error')('attribute')));
      }
    };

    getAttributes();
  }, 500);

  useEffect(() => {
    if (internalSiteSource && isHeightReady) {
      onRequestAttributes();
    }
    // onRequestAttributes is intentionally omitted: useDebounce doesn't guarantee a
    // stable reference across renders, so including it can trigger repeated fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    internalSiteSource,
    isHeightReady,
    baseQuery,
    searchFilter,
    compFilter,
    needsRefresh,
    height,
  ]);

  const isNewAttribute = !attribute?.id;
  const schema = useMemo(
    () => getAttributeSchema(components, isNewAttribute),
    [components, isNewAttribute]
  );

  const attributeCustomValidate = useCallback(
    (formData, validationErrors) => addRequiredFieldErrors(formData, validationErrors, schema),
    [schema]
  );

  const attributeTransformErrors = useCallback(
    (validationErrors) => suppressRequiredFieldErrors(validationErrors, schema),
    [schema]
  );

  const onChangeAttribute = useCallback(
    ({ formData }) => {
      if (formData.name !== attribute.name) {
        setExtraErrors({});
      }

      if (formData.dataType === attribute.dataType) {
        setAttribute(formData);
      } else {
        setAttribute({
          ...formData,
          lastValue: formData.dataType === 'boolean' ? 'true' : undefined,
        });
      }
    },
    [attribute]
  );

  const onSubmitAttribute = useCallback(
    ({ formData }) => {
      const payload = cleanFormData(formData);
      let value = payload.lastValue;
      if (payload.dataType === 'boolean') {
        value = payload.lastValue === 'true';
      }
      payload.values = [{ value, timestamp: new Date().toISOString() }];

      const successCallback = () => {
        setPage(1);
        setExtraErrors({});
        setAttribute(null);
        setNeedsRefresh((previous) => previous + 1);
        dispatch(requestComponents(siteId));
      };

      const failureCallback = (errors) => {
        setExtraErrors({ name: { __errors: errors } });
      };

      if (isNewAttribute) {
        dispatch(
          createVariables(
            internalSiteSource.id,
            { source: internalSiteSource.id, ...payload },
            successCallback,
            failureCallback
          )
        );
      } else {
        dispatch(
          updateVariable(payload.source_id, payload.id, payload, successCallback, failureCallback)
        );
      }
    },
    [isNewAttribute, internalSiteSource, dispatch, siteId]
  );

  const onCloseAttributeModal = useCallback(() => {
    setAttribute(null);
    setExtraErrors({});
  }, []);

  const onDeleteAttribute = useCallback(
    (attribute) => {
      dispatch(
        deleteVariable(attribute.source_id, attribute.id, true, () => {
          setPage(1);
          setNeedsRefresh((previous) => previous + 1);
          dispatch(requestComponents(siteId));
        })
      );
    },
    [dispatch, siteId]
  );

  const headers = [
    { label: 'Name' },
    { label: 'Data type' },
    { label: 'Category' },
    { label: 'Latest Value' },
    { label: 'Component' },
  ];

  const headerColumns = headers.map((header, index) => (
    <div key={`${header.label}-${index}`}>{header.label}</div>
  ));

  const getListItemColumns = useCallback(
    (attribute) => {
      const component = components.find((comp) => comp.id === attribute?.asset?.component.id);
      return component
        ? [
            <div
              key={`${component.id}-col-1`}
              className="ellipsed-text"
            >
              {attribute.name}
            </div>,
            <div
              key={`${component.id}-col-2`}
              className="ellipsed-text"
            >
              {attribute.dataType[0].toUpperCase() + attribute.dataType.slice(1)}
            </div>,
            <div
              key={`${component.id}-col-3`}
              className="ellipsed-text"
            >
              {attribute.category}
            </div>,
            <div
              key={`${component.id}-col-4`}
              className="ellipsed-text"
            >
              {attribute.lastValue?.toString()}
            </div>,
            <div
              key={`${component.id}-col-5`}
              className="blocked-item-container"
            >
              <div className="blocked-item">
                {`${component.name} (${component.itemDesignation})`}
              </div>
            </div>,
          ]
        : [];
    },
    [components]
  );

  const columnWidths = COLUMN_WIDTHS;

  const attributeList = useMemo(
    () => (
      <div className="custom-thin-scrollbar">
        {attributes &&
          attributes.map((attribute) => (
            <ListItem
              key={attribute.id}
              entity={`Attribute (${attribute.name})`}
              item={attribute}
              columns={getListItemColumns(attribute)}
              columnWidths={columnWidths}
              customEdit
              onEdit={() => setAttribute(attribute)}
              onDelete={() => onDeleteAttribute(attribute)}
              deletePermission="variables/Delete"
              permissionScope={{ org, site: siteId }}
              confirmationDialogTitle={'Delete attribute'}
              confimationDialogBody={<p>Are you sure you want to remove this attribute?</p>}
            />
          ))}
      </div>
    ),
    [attributes, getListItemColumns, onDeleteAttribute, org, siteId, columnWidths]
  );

  const onSearchFilter = useCallback((event) => {
    setPage(1);
    setSearchFilter((event.target.value || '').trim());
  }, []);

  const onComponentFilter = useCallback((value) => {
    setPage(1);
    setCompFilter(value);
  }, []);

  return (
    <>
      <Heading
        contentLeft={
          <div className="attributes-header">
            <div className="title">Attributes</div>
            <Input
              type="text"
              onChange={onSearchFilter}
              value={searchFilter}
              placeholder="Search attribute name"
            />

            <div className="asset-select">
              <ComponentSelect
                isMulti={false}
                value={compFilter}
                availableComponents={components.filter((comp) => comp.asset.variables.length)}
                onChange={onComponentFilter}
                nullOptionText="All components"
                useAssetIds
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: '100%',
                    height: '36px',
                    minHeight: '36px',
                  }),
                }}
              />
            </div>
          </div>
        }
        contentRight={
          <Button
            disabled={!internalSiteSource}
            onClick={() => setAttribute({})}
            className={`button-create`}
          >
            Add Attribute
          </Button>
        }
      />
      <div className="manage-attributes-body">
        <p className="attributes">{`Manage attributes (${totalAttributes})`}</p>
        <div
          className="list-container"
          ref={clientReference}
        >
          <ListItem
            isHeader
            columns={headerColumns}
            columnWidths={columnWidths}
          />

          {attributes ? (
            attributeList
          ) : (
            <div className="attribute-loader">
              <Spinner
                size="s"
                className="spinner"
              />
            </div>
          )}
        </div>
        <Pagination
          pages={Math.ceil(totalAttributes / queryLimit)}
          page={page}
          setPage={setPage}
        />
      </div>
      {attribute && componentsLoaded && (
        <EditItemModal
          entity="Attribute"
          action={attribute.id ? 'Edit' : 'Add'}
          formData={attribute}
          schema={schema}
          uiSchema={attributeUiSchema}
          onSubmit={onSubmitAttribute}
          onChange={onChangeAttribute}
          onCloseModal={onCloseAttributeModal}
          withTitle={false}
          extraErrors={extraErrors}
          customValidate={attributeCustomValidate}
          customTransformErrors={attributeTransformErrors}
          flexEndButtons
        />
      )}
    </>
  );
};

export default AttributeView;
